/**
 * Imperative modal rendering utility.
 *
 * Provides a thin wrapper around OrnaReact.renderComponent / destroyComponent
 * so that callers don't have to manually create containers, append them to
 * the body, or clean up DOM nodes.
 *
 * Usage:
 *   const handle = renderImperativeModal(SomeModalReact, { open: true, ... });
 *   handle.update({ open: false });   // re-render with new props
 *   handle.destroy();                 // unmount & remove from DOM
 */

export interface ImperativeModalHandle {
  /** Re-render the component with new props. */
  update(props: Record<string, any>): void;
  /** Unmount the React tree and remove the container element from the DOM. */
  destroy(): void;
  /** The container element that was appended to document.body. */
  container: HTMLElement;
}

/**
 * Mount a React component imperatively (outside the main React tree).
 *
 * @param component  The React component reference (e.g. window.SomeModalReact)
 * @param initialProps  Props to pass on first render
 * @returns A handle with `update`, `destroy`, and `container` members
 */
export function renderImperativeModal(
  component: any,
  initialProps: Record<string, any>,
): ImperativeModalHandle {
  const container = document.createElement('div');
  document.body.appendChild(container);

  const OrnaReact = (window as any).OrnaReact;
  OrnaReact.renderComponent(component, initialProps, container);

  return {
    container,
    update(props: Record<string, any>) {
      OrnaReact.renderComponent(component, props, container);
    },
    destroy() {
      OrnaReact.destroyComponent(container);
      if (container.parentNode) {
        container.parentNode.removeChild(container);
      }
    },
  };
}
